import React from 'react';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { DollarSign, Heart, ExternalLink } from 'lucide-react';

const Giving = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="mb-8 animate-fade-in">
          <h1 className="text-4xl font-heading font-bold text-[#2B6B7F] mb-2">
            Giving
          </h1>
          <p className="text-slate-600">Support the ministry of Westland Free Methodist Church</p>
        </div>

        {/* Info Card */}
        <Card className="mb-6 border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-green-900 mb-2">Thank You for Your Generosity</h3>
                <p className="text-sm text-green-800 mb-3">
                  Your financial support helps us fulfill our mission to make disciples of Jesus Christ who love God, 
                  love others, and serve the world. Every gift makes a difference in our community and beyond.
                </p>
                <p className="text-sm text-green-700 italic">
                  "Each of you should give what you have decided in your heart to give, not reluctantly or under 
                  compulsion, for God loves a cheerful giver." - 2 Corinthians 9:7
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Give Now — direct handoff to the WFMC PayPal giving form */}
        <Card className="overflow-hidden mb-6">
          <CardHeader className="bg-gradient-to-r from-[#2B6B7F] to-[#5FA8C1]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-white text-xl">Online Giving with PayPal</CardTitle>
                <CardDescription className="text-white/90 text-sm">
                  Secure one-time or recurring giving through PayPal
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="py-6 space-y-5">
            <div className="max-w-xl mx-auto">
              <p className="font-semibold text-slate-800 mb-3 text-center">
                Three quick steps when the page opens:
              </p>
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2B6B7F] text-white flex items-center justify-center text-sm font-bold">
                    1
                  </span>
                  <div className="text-sm text-slate-700">
                    Tap the{' '}
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 text-amber-900 border border-amber-300 font-semibold">
                      ONLINE with PAYPAL
                    </span>{' '}
                    tab at the top (next to <em>By Mail</em>).
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2B6B7F] text-white flex items-center justify-center text-sm font-bold">
                    2
                  </span>
                  <div className="text-sm text-slate-700">
                    Scroll down and fill out the giving form (name, email, amount, fund).
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2B6B7F] text-white flex items-center justify-center text-sm font-bold">
                    3
                  </span>
                  <div className="text-sm text-slate-700">
                    Tap <strong>Submit</strong> — you&apos;ll be taken to PayPal to complete your gift.
                  </div>
                </li>
              </ol>
            </div>
            <div className="text-center pt-1">
              <a
                href="https://www.westlandfmc.com/giving/#service-locations"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-lg bg-gradient-to-r from-[#2B6B7F] to-[#5FA8C1] text-white font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
                data-testid="giving-give-now-btn"
              >
                <DollarSign className="w-5 h-5" />
                Open Giving Page
                <ExternalLink className="w-4 h-4 opacity-80" />
              </a>
              <p className="text-xs text-slate-500 mt-2">
                Opens westlandfmc.com in a new tab — return to the app when you&apos;re done.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Additional Information */}
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-[#2B6B7F]">Other Ways to Give</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-slate-700">
              <div>
                <strong>In Person:</strong> Place your gift in the offering box during service
              </div>
              <div>
                <strong>By Mail:</strong> Westland Free Methodist Church, 33445 Warren Rd, Westland, MI 48185
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-[#2B6B7F]">Questions?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-slate-700">
              <p>
                If you have questions about giving or need assistance, please contact the church office.
              </p>
              <div>
                <strong>Phone:</strong> (734) 728-2600
              </div>
              <div>
                <strong>Email:</strong> office@westlandfmc.com
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Giving;
